import React, { useState, useEffect, useRef } from 'react';
import PatientPortalRouter from '../PatientPortalRouter/PatientPortalRouter';
import { Link, useLocation } from 'react-router-dom';
import { useNavigation } from '../../../utils/redirectUtils';
import Popper from "@mui/material/Popper";
import Grow from "@mui/material/Grow";
import ClickAwayListener from "@mui/material/ClickAwayListener";

import styles from './header.module.css';

import logo from '../../../assets/logo.png';
import msg from '../../../assets/msg.png';
import menu from '../../../assets/menu.png';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import LocalLibraryIcon from '@mui/icons-material/LocalLibrary';
import HomeIcon from '@mui/icons-material/Home';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ImportContactsIcon from '@mui/icons-material/ImportContacts';
import PhoneIcon from '@mui/icons-material/Phone';
import MailIcon from '@mui/icons-material/Mail';
import LoginIcon from '@mui/icons-material/Login';
import PaidIcon from '@mui/icons-material/Paid';
import HandshakeIcon from '@mui/icons-material/Handshake';


function Header({ hideNavigation = false, nonSticky = false, transparent = false }) {
    const navigate = useNavigation();
    const location = useLocation();
    const [open, setOpen] = React.useState(false);
    const [patientRouterOpen, setPatientRouterOpen] = useState(false);
    const [isScrolling, setIsScrolling] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [mailMenuOpen, setMailMenuOpen] = useState(false);
    const elementRef = useRef(null);
    const mailMenuRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (mailMenuRef.current && !mailMenuRef.current.contains(event.target)) {
                setMailMenuOpen(false);
            }
        };

        const handleEscapeKey = (event) => {
            if (event.key === 'Escape') {
                setMailMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscapeKey);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, []);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            {
                threshold: 0.5,
            }
        );

        let currentElement = elementRef.current;
        if (currentElement) {
            observer.observe(currentElement);
        }

        return () => {
            if (currentElement) {
                observer.unobserve(currentElement);
            }
        };
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolling(window.scrollY > 0);
        };

        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    const toggleDrawer = (newOpen) => () => {
        setOpen(newOpen);
    };

    const DrawerList = (
        <Box sx={{ width: 275 }} role="presentation" onClick={toggleDrawer(false)}>
            <List>
                <ListItem disablePadding>
                    <ListItemButton to='/'>
                        <ListItemIcon>
                            <HomeIcon />
                        </ListItemIcon>
                        <ListItemText primary="Home" />
                    </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                    <ListItemButton to='/programs'>
                        <ListItemIcon>
                            <LocalLibraryIcon />
                        </ListItemIcon>
                        <ListItemText primary="Weight Loss Programs" />
                    </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                    <ListItemButton to='/knowledge-center'>
                        <ListItemIcon>
                            <VerifiedUserIcon />
                        </ListItemIcon>
                        <ListItemText primary="Knowledge Center" />
                    </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                    <ListItemButton to='/success-stories'>
                        <ListItemIcon>
                            <FavoriteIcon />
                        </ListItemIcon>
                        <ListItemText primary="Success Stories" />
                    </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                    <ListItemButton to='/articles'>
                        <ListItemIcon>
                            <ImportContactsIcon />
                        </ListItemIcon>
                        <ListItemText primary="Articles" />
                    </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                    <ListItemButton to='/affiliates'>
                        <ListItemIcon>
                            <HandshakeIcon />
                        </ListItemIcon>
                        <ListItemText primary="Affiliates" />
                    </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                    <ListItemButton onClick={() => setPatientRouterOpen(true)}>
                        <ListItemIcon>
                            <LoginIcon />
                        </ListItemIcon>
                        <ListItemText primary="Patient Portal" />
                    </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                    <ListItemButton to='/billing'>
                        <ListItemIcon>
                            <PaidIcon />
                        </ListItemIcon>
                        <ListItemText primary="Billing" />
                    </ListItemButton>
                </ListItem>
            </List>
        </Box>
    );

    return (
        <div data-header-root className={`${styles.headerContainer} ${isScrolling ? styles.scrollingHeader : ''} ${nonSticky ? styles.nonSticky : ''} ${transparent ? styles.transparentHeader : ''}`}>
            <PatientPortalRouter open={patientRouterOpen} onClose={() => setPatientRouterOpen(false)} />
            <div>
                <Drawer anchor="right" open={open} onClose={toggleDrawer(false)}>
                    {DrawerList}
                </Drawer>
            </div>
            <div className={styles.mainSection}>

                <Link to="/" className={styles.logoLink}>
                    <img className={styles.tb2Logo} src={logo} alt='TB2' />
                </Link>

                <div className={styles.interactionsSection}>

                    {!hideNavigation && (
                        <div className={styles.headerTabs}>
                            <Link
                                to="/"
                                className={`${styles.headerTab} ${location.pathname === '/' ? styles.activeTab : ''}`}
                            >
                                Home
                            </Link>

                            <Link
                                to="/programs"
                                className={`${styles.headerTab} ${location.pathname.startsWith('/programs') ? styles.activeTab : ''}`}
                            >
                                Programs
                            </Link>

                            <Link
                                to="/knowledge-center"
                                className={`${styles.headerTab} ${location.pathname.startsWith('/knowledge-center') ? styles.activeTab : ''}`}
                            >
                                Knowledge Center
                            </Link>

                            <Link
                                to="/success-stories"
                                className={`${styles.headerTab} ${location.pathname.startsWith('/success-stories') ? styles.activeTab : ''}`}
                            >
                                Success Stories
                            </Link>

                            <Link
                                to="/articles"
                                className={`${styles.headerTab} ${location.pathname.startsWith('/articles') ? styles.activeTab : ''}`}
                            >
                                Articles
                            </Link>

                            <Link
                                to="/affiliates"
                                className={`${styles.headerTab} ${location.pathname.startsWith('/affiliates') ? styles.activeTab : ''}`}
                            >
                                Affiliates
                            </Link>

                            <button
                                onClick={() => {process.env.REACT_APP_PATIENT_PORTAL_ENABLED === 'true' ? setPatientRouterOpen(true) : window.open('https://patient.tb2.health', '_blank', 'noopener,noreferrer')}}
                                className={styles.headerTab}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, font: 'inherit' }}
                            >
                                Patient Portal
                            </button>
                            <Link
                                to="/billing"
                                className={styles.headerTab}
                            >
                                Billing
                            </Link>
                        </div>
                    )}

                    <div className={styles.buttonsHeader}>
                        <div className={styles.contactContainerWrapper}>
                            <div className={styles.contactContainer}>
                                <a href="tel:18445703201">
                                    <PhoneIcon style={{ color: '#0D79FE', verticalAlign: 'middle' }} />
                                    <span className={styles.contactText}> 844.570.3201</span>
                                </a>
                            </div>
                        </div>
                        <div className={styles.contactContainerWrapper}>
                            <div className={styles.contactContainer} ref={mailMenuRef}>
                                <button
                                    type="button"
                                    className={styles.mailButton}
                                    onClick={() => setMailMenuOpen((v) => !v)}
                                    aria-haspopup="true"
                                    aria-expanded={mailMenuOpen}
                                    aria-label="Email options"
                                >
                                    <MailIcon style={{ color: '#0D79FE', verticalAlign: 'middle' }} />
                                </button>

                                <Popper open={mailMenuOpen} anchorEl={mailMenuRef.current} placement="bottom-end" transition>
                                    {({ TransitionProps }) => (
                                    <Grow {...TransitionProps} timeout={250}>
                                        <div>
                                        <ClickAwayListener onClickAway={() => setMailMenuOpen(false)}>
                                            <div className={styles.mailDropdown}>
                                                <div className={styles.mailDropdownItemContainer}>
                                                    <a
                                                        className={styles.mailDropdownItem}
                                                        href={`mailto:social@tb2.health?subject=${encodeURIComponent('Question about the product')}`}
                                                        onClick={() => setMailMenuOpen(false)}
                                                    >
                                                        <span className={styles.mailDropdownItemText}>Questions about the product?</span>
                                                    </a>
                                                </div>

                                                <div className={styles.mailDropdownItemContainer}>
                                                    <a
                                                        className={styles.mailDropdownItem}
                                                        href={`mailto:support@tb2.health?subject=${encodeURIComponent('Question regarding my subscription')}`}
                                                        onClick={() => setMailMenuOpen(false)}
                                                    >
                                                        <span className={styles.mailDropdownItemText}>Customer support?</span>
                                                    </a>
                                                </div>
                                            </div>
                                        </ClickAwayListener>
                                        </div>
                                    </Grow>
                                    )}
                                </Popper>
                            </div>
                        </div>


                        {!hideNavigation && (
                            <img className={styles.menuIcon} style={{ cursor: "pointer" }} onClick={toggleDrawer(true)} src={menu} alt='menu' />
                        )}
                        
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Header;
